import React, { useCallback, useState } from "react";
import { aboutText, stats, endorsements, experience, personalInfo } from "../../data/portfolio";
import Reveal from "../motion/Reveal";
import SplitLines from "../motion/SplitLines";
import Decode from "../motion/Decode";
import Ruler from "../motion/Ruler";
import Halftone from "../ui/Halftone";

const paragraphs = aboutText
  .split(/\n\s*\n/)
  .map((p) => p.trim())
  .filter(Boolean);

const [opening, ...rest] = paragraphs;
const closing = rest.pop();

const activeRoles = experience.filter((role) => role.period.includes("Present"));

const CENTRE = { x: 0.5, y: 0.5 };

const About = () => {
  const [showPhoto, setShowPhoto] = useState(false);
  /* The dissolve ripples out from wherever the pointer crossed the edge. */
  const [origin, setOrigin] = useState(CENTRE);
  const [plateReady, setPlateReady] = useState(false);

  const enter = useCallback((event) => {
    const box = event.currentTarget.getBoundingClientRect();
    setOrigin({
      x: (event.clientX - box.left) / box.width,
      y: (event.clientY - box.top) / box.height,
    });
    setShowPhoto(true);
  }, []);

  const leave = useCallback(() => setShowPhoto(false), []);

  return (
    <section id="about" className="chapter chapter-pad relative" aria-labelledby="about-title">
    <div className="page">
      {/* Chapter header */}
      <div className="flex flex-wrap items-baseline justify-between gap-4 pb-4">
        <h2 id="about-title" className="t-label">
          <span className="text-flare">02</span> — Signal
          <span className="sr-only"> — how I work</span>
        </h2>
        <span className="t-label">How I work</span>
      </div>
      <Ruler ticks={16} />

      <div className="mt-12 grid-12 gap-y-14 md:mt-20">
        {/* Machine-vision portrait */}
        <div className="col-span-12 md:col-span-5 lg:col-span-4">
          <Reveal>
            <button
              type="button"
              className="media-frame block aspect-[4/5] w-full cursor-default bg-surface"
              onMouseEnter={enter}
              onMouseLeave={leave}
              onFocus={() => {
                setOrigin(CENTRE);
                setShowPhoto(true);
              }}
              onBlur={leave}
              onClick={() => {
                setOrigin(CENTRE);
                setShowPhoto((prev) => !prev);
              }}
              aria-pressed={showPhoto}
              aria-label={
                showPhoto
                  ? `Photo of ${personalInfo.fullName}. Activate to show the halftone version.`
                  : `Halftone version of a photo of ${personalInfo.fullName}. Activate to show the photo.`
              }
              data-cursor-label={showPhoto ? "Halftone" : "Photo"}
            >
              <span className="relative block h-full w-full">
                {/* the real photo sits underneath the whole time */}
                <img
                  src="/me.webp"
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {/* Holds the photo covered until the plate has actually been
                    drawn; after that the canvas paints its own ground. */}
                <span
                  className="absolute inset-0 block"
                  style={{
                    background: "var(--bg)",
                    opacity: plateReady ? 0 : 1,
                    transition: "opacity 200ms linear",
                  }}
                />
                <Halftone
                  src="/me.webp"
                  alt=""
                  cell={7}
                  className="absolute inset-0 h-full w-full"
                  dissolved={showPhoto}
                  origin={origin}
                  onReady={() => setPlateReady(true)}
                />
              </span>
            </button>
            <div className="hair-t mt-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pt-2.5">
              <span className="t-label">Printed as dots</span>
              <span className="t-label-strong">
                <span className="hidden lg:inline">Hover</span>
                <span className="lg:hidden">Tap</span> for the photo
              </span>
            </div>
          </Reveal>
        </div>

        {/* Manifesto */}
        <div className="col-span-12 md:col-span-7 lg:col-span-5 lg:col-start-6">
          <p
            className="t-serif text-flare"
            style={{ fontSize: "clamp(1.75rem, 4vw, 3.25rem)", lineHeight: 1.05 }}
          >
            <span className="text-fg">Systems that</span> hold up
            <span className="text-fg"> in the real world.</span>
          </p>

          <SplitLines
            as="p"
            className="t-body mt-8"
            stagger={0.07}
          >
            {opening}
          </SplitLines>

          {rest.map((paragraph) => (
            <SplitLines as="p" key={paragraph.slice(0, 24)} className="t-body mt-5" stagger={0.07}>
              {paragraph}
            </SplitLines>
          ))}

          <Reveal className="hair-l mt-9 pl-5">
            <p className="t-mono max-w-[52ch] leading-relaxed text-dim">{closing}</p>
          </Reveal>
        </div>

        {/* Readouts */}
        <aside className="col-span-12 lg:col-span-2 lg:col-start-11" aria-label="Profile readouts">
          <Reveal stagger={0.07} className="grid grid-cols-2 gap-x-6 gap-y-7 lg:grid-cols-1">
            {stats.map((stat) => (
              <div key={stat.label} data-reveal-item className="hair-t pt-2.5">
                <span className="t-label block">{stat.label}</span>
                <span className="mt-1 block text-sm leading-snug text-fg">{stat.value}</span>
              </div>
            ))}
            <div data-reveal-item className="hair-t pt-2.5">
              <span className="t-label block">Where I work</span>
              <ul className="mt-1 space-y-1.5">
                {activeRoles.map((role) => (
                  <li key={role.id} className="text-sm leading-snug text-fg">
                    {role.company}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </aside>
      </div>

      {/* Endorsements */}
      <div className="mt-20 md:mt-28">
        <div className="hair-b flex items-baseline justify-between pb-4">
          <span className="t-label">What people say</span>
          <span className="t-label">{endorsements.length} notes</span>
        </div>

        <Reveal stagger={0.09} className="grid items-stretch gap-x-8 gap-y-10 pt-9 md:grid-cols-3">
          {endorsements.map((item) => (
            <figure key={item.author} data-reveal-item className="flex h-full flex-col gap-5">
              <blockquote className="text-[0.9375rem] leading-relaxed text-fg md:text-base">
                {item.quote}
              </blockquote>
              <figcaption className="hair-t mt-auto flex items-center gap-3 pt-3.5">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.author}
                    width={40}
                    height={40}
                    loading="lazy"
                    className="h-10 w-10 object-cover"
                    style={{ filter: "grayscale(1) contrast(1.05)" }}
                  />
                ) : (
                  <span
                    className="t-num flex h-10 w-10 items-center justify-center text-[0.6875rem] text-dim"
                    style={{ border: "1px solid var(--line)" }}
                    aria-hidden="true"
                  >
                    {item.author.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                  </span>
                )}
                <span className="block">
                  <span className="block text-sm text-fg">{item.author}</span>
                  <span className="t-label block">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </Reveal>
      </div>
    </div>
    </section>
  );
};

export default About;
